import { useState } from "react";
import { Play } from "lucide-react";
import { VIDEOS } from "../mock/mock";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import useReveal from "../hooks/useReveal";
import { useConsent } from "../context/ConsentContext";

function VideoCard({ video, onOpen }) {
  return (
    <article className="group rounded-2xl bg-white shadow-md overflow-hidden transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl">
      <button
        onClick={() => onOpen(video)}
        className="relative block w-full aspect-video overflow-hidden"
        data-testid={`video-open-${video.id}`}
      >
        <img
          src={`https://img.youtube.com/vi/${video.youtubeId}/hqdefault.jpg`}
          alt={video.title}
          width="480"
          height="360"
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute inset-0 flex items-center justify-center bg-black/25">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#5c2751] text-white shadow-lg">
            <Play className="h-6 w-6 fill-white ml-1" />
          </span>
        </span>
      </button>
      <div className="p-5">
        <span className="text-xs uppercase tracking-wider font-bold text-[#5c2751]">
          {video.title}
        </span>
        <h3 className="mt-1 font-heading text-lg font-bold text-[#4a1f42]">
          {video.subtitle}
        </h3>
        <p className="mt-2 text-sm text-[#6c6c6c] leading-relaxed">
          {video.description}
        </p>
      </div>
    </article>
  );
}

export default function Videos() {
  const [active, setActive] = useState(null);
  const [ref, inView] = useReveal();
  const { consent, grantExternal } = useConsent();
  const externalAllowed = consent?.external === true;

  return (
    <section id="videos" className="bg-white py-16 md:py-24">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-[#5c2751]">
            Les vidéos souvenirs
          </h2>
          <p className="mt-4 text-[#6c6c6c]">
            Des voyages, des regards et des rencontres sur le pèlerinage de
            Saint-Jacques-de-Compostelle.
          </p>
        </div>

        <div
          ref={ref}
          className={`mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-7 ${inView ? "reveal in-view" : "reveal"}`}
        >
          {VIDEOS.map((v) => (
            <VideoCard key={v.id} video={v} onOpen={setActive} />
          ))}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="w-[calc(100%-1.5rem)] max-w-3xl p-0 overflow-hidden rounded-xl bg-[#151515] border-0">
          <DialogHeader className="px-5 pt-4 md:px-6 md:pt-5">
            <DialogTitle className="pr-8 text-left font-heading text-xl md:text-2xl text-white">
              {active?.subtitle}
            </DialogTitle>
          </DialogHeader>
          <div className="aspect-video w-full">
            {active &&
              (externalAllowed ? (
                <iframe
                  className="h-full w-full"
                  src={`https://www.youtube-nocookie.com/embed/${active.youtubeId}?autoplay=1`}
                  title={active.subtitle}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              ) : (
                <div className="flex h-full flex-col items-center justify-center gap-4 bg-[#151515] px-6 text-center">
                  <p className="max-w-sm text-sm leading-relaxed text-white/80">
                    Cette vidéo nécessite l'autorisation des cookies de contenu
                    externe (YouTube).
                  </p>
                  <button
                    data-testid="video-allow-external"
                    onClick={grantExternal}
                    className="rounded-full bg-[#5c2751] px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#4a1f42]"
                  >
                    Autoriser et afficher la vidéo
                  </button>
                  <a
                    href="/politique-cookies"
                    className="text-xs text-white/50 underline underline-offset-2 hover:text-white/80"
                  >
                    En savoir plus
                  </a>
                </div>
              ))}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
